package com.elan.edubridge.shared.di

import com.elan.edubridge.shared.data.datasource.AcademyCommandDataSource
import com.elan.edubridge.shared.data.datasource.AccountDataSource
import com.elan.edubridge.shared.data.datasource.EduConnectDataSource
import com.elan.edubridge.shared.data.datasource.InMemoryAcademyCommandDataSource
import com.elan.edubridge.shared.data.datasource.InMemoryAccountDataSource
import com.elan.edubridge.shared.data.datasource.InMemoryEduConnectDataSource
import com.elan.edubridge.shared.data.repository.AcademyCommandRepositoryImpl
import com.elan.edubridge.shared.data.repository.AccountRepositoryImpl
import com.elan.edubridge.shared.data.repository.EduConnectRepositoryImpl
import com.elan.edubridge.shared.domain.repository.AcademyCommandRepository
import com.elan.edubridge.shared.domain.repository.AccountRepository
import com.elan.edubridge.shared.domain.repository.EduConnectRepository
import com.elan.edubridge.shared.domain.usecase.GetAttendanceStatsUseCase
import com.elan.edubridge.shared.domain.usecase.GetClassRosterUseCase
import com.elan.edubridge.shared.domain.usecase.GetSectionItemsUseCase
import com.elan.edubridge.shared.domain.usecase.GetTodayDashboardUseCase
import com.elan.edubridge.shared.domain.usecase.ObserveAttendanceFeedUseCase
import com.elan.edubridge.shared.domain.usecase.ObserveMyTimelineUseCase
import com.elan.edubridge.shared.presentation.AttendanceFeedObserver
import com.elan.edubridge.shared.presentation.SessionObserver
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

/**
 * 컴포지션 루트. domain·data·presentation을 아는 유일한 지점이다.
 *
 * **모든 인스턴스가 lazy다.** 이전에는 `val`이 클래스 초기화 시점에 즉시 생성돼
 * [install]을 나중에 부르면 플랫폼 소스가 조용히 무시되고 계속 데모 데이터가 보였다.
 * JS는 `main()` 순서 덕에 우연히 살았지만 Android(Firebase ContentProvider + Application)에서는
 * 반드시 깨진다. 이제 첫 `create*` 이후의 [install]은 조용히 무시되는 대신 즉시 실패한다.
 *
 * 아무도 [install]하지 않으면 InMemory 소스로 폴백한다 — 데모·테스트는 지금까지처럼 동작한다.
 */
object SharedGraph {
    private val graphScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private var installedAccountDataSource: AccountDataSource? = null
    private var installedCatalogDataSource: EduConnectDataSource? = null
    private var installedCommandDataSource: AcademyCommandDataSource? = null
    private var resolved = false

    /**
     * 플랫폼 데이터소스를 주입한다. **첫 `create*` 호출 이전에 한 번만** 가능하다.
     *
     * 기본 인자를 두지 않는다 — Kotlin 기본값은 ObjC 헤더에 노출되지 않아 Swift에서 사라진다.
     */
    fun install(
        accountDataSource: AccountDataSource,
        catalogDataSource: EduConnectDataSource,
        commandDataSource: AcademyCommandDataSource,
    ) {
        check(!resolved) {
            "SharedGraph.install()은 첫 create* 호출 이전에만 가능하다."
        }
        installedAccountDataSource = accountDataSource
        installedCatalogDataSource = catalogDataSource
        installedCommandDataSource = commandDataSource
    }

    private val catalogDataSource: EduConnectDataSource by lazy {
        resolved = true
        installedCatalogDataSource ?: InMemoryEduConnectDataSource()
    }

    private val accountDataSource: AccountDataSource by lazy {
        resolved = true
        installedAccountDataSource ?: InMemoryAccountDataSource()
    }

    private val commandDataSource: AcademyCommandDataSource by lazy {
        resolved = true
        installedCommandDataSource ?: InMemoryAcademyCommandDataSource()
    }

    private val repository by lazy { EduConnectRepositoryImpl(catalogDataSource) }

    /** 세션 StateFlow가 프로세스 전체에서 하나로 유지되도록 인스턴스를 캐시한다. */
    private val accountRepository by lazy { AccountRepositoryImpl(accountDataSource, graphScope) }

    private val commandRepository by lazy { AcademyCommandRepositoryImpl(commandDataSource) }

    private val getSectionItemsUseCase by lazy { GetSectionItemsUseCase(repository) }

    private val getTodayDashboardUseCase by lazy { GetTodayDashboardUseCase(repository) }

    private val observeAttendanceFeedUseCase by lazy { ObserveAttendanceFeedUseCase(repository) }

    private val getClassRosterUseCase by lazy { GetClassRosterUseCase(repository) }

    private val observeMyTimelineUseCase by lazy { ObserveMyTimelineUseCase(repository) }

    private val getAttendanceStatsUseCase by lazy { GetAttendanceStatsUseCase(repository) }

    fun createRepository(): EduConnectRepository = repository

    fun createAccountRepository(): AccountRepository = accountRepository

    fun createAcademyCommandRepository(): AcademyCommandRepository = commandRepository

    fun createGetSectionItemsUseCase(): GetSectionItemsUseCase = getSectionItemsUseCase

    fun createGetTodayDashboardUseCase(): GetTodayDashboardUseCase = getTodayDashboardUseCase

    fun createObserveAttendanceFeedUseCase(): ObserveAttendanceFeedUseCase =
        observeAttendanceFeedUseCase

    fun createGetClassRosterUseCase(): GetClassRosterUseCase = getClassRosterUseCase

    fun createObserveMyTimelineUseCase(): ObserveMyTimelineUseCase = observeMyTimelineUseCase

    fun createGetAttendanceStatsUseCase(): GetAttendanceStatsUseCase = getAttendanceStatsUseCase

    fun createSessionObserver(): SessionObserver =
        SessionObserver(accountRepository, graphScope)

    /** Swift가 출결 피드를 읽는 유일한 경로다 — 유스케이스는 `Flow`라 ObjC로 나가지 않는다. */
    fun createAttendanceFeedObserver(): AttendanceFeedObserver =
        AttendanceFeedObserver(observeAttendanceFeedUseCase, graphScope)
}
